import { useCountryFilteredProductAds, useProductAds } from '@/domain/product-ads/hooks';

import useEmblaCarousel from 'embla-carousel-react';
import { styled } from 'styled-components';
import { Banner, horizontalGap, tk, Typography } from '@sunwebgroup/ui-components';
import { useCallback, useEffect, useState } from 'react';
import { ProductAdLoading } from './ProductAdLoading';
import { useIntl } from '@/intl';
import { type ProductAd } from '@/domain/product-ads/types';
import { ProductAd as ProductAdComponent } from './ProductAd';

const Slider = styled.div`
  overflow: hidden;
  width: calc(100% + ${tk('spacing_xl')});
  margin-left: -${tk('spacing_md')};
  margin-right: -${tk('spacing_md')};

  @media ${tk('mq_desktop_sm')} {
    width: inherit;
    margin-left: -${tk('spacing_3xl')};
    margin-right: -${tk('spacing_3xl')};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${tk('spacing_2xs')}; // needed to show the shadow of the items
  @media ${tk('mq_desktop_lg')} {
    width: 800px;
  }
  @media ${tk('mq_desktop_xl')} {
    width: 940px;
  }
  ${horizontalGap('spacing_md')}
`;

const FaddingWrapper = styled.div`
  &:before {
    background-image: linear-gradient(270deg, hsla(0, 0%, 100%, 0), ${tk('theme_color_neutral_300')});
    bottom: ${tk('spacing_md')};
    content: '';
    display: block;
    left: 0;
    position: absolute;
    width: ${tk('spacing_md')};
    height: calc(100% - ${tk('spacing_xl')});
    z-index: 1;
  }

  &:after {
    background-image: linear-gradient(90deg, hsla(0, 0%, 100%, 0), ${tk('theme_color_neutral_300')});
    bottom: ${tk('spacing_md')};
    content: '';
    display: block;
    right: 0;
    position: absolute;
    width: ${tk('spacing_md')};
    height: calc(100% - ${tk('spacing_xl')});
    z-index: 1;
  }

  @media ${tk('mq_desktop_sm')} {
    &:before {
      width: ${tk('spacing_2xl')};
    }

    &:after {
      background-image: linear-gradient(90deg, hsla(0, 0%, 100%, 0), ${tk('color_semantic_bg')});
      width: ${tk('spacing_2xl')};
    }
  }

  ${Wrapper} {
    width: 100%;
  }
`;

const ProductAdWrapper = styled.div`
  &:first-child {
    margin-left: ${tk('spacing_sm')};
  }

  &:last-child {
    margin-right: ${tk('spacing_sm')};
  }

  @media ${tk('mq_desktop_sm')} {
    &:first-child {
      margin-left: ${tk('spacing_2xl')};
    }

    &:last-child {
      margin-right: ${tk('spacing_2xl')};
    }
  }
`;

interface ProductAdsListProps {
  noFading?: boolean;
  data: ProductAd[] | undefined;
  isLoading?: boolean;
}

export function HomepageProductAdsList({ contextItemId, noFading }: { contextItemId: string; noFading?: boolean }) {
  const { isLoading, data } = useProductAds(contextItemId);
  return <ProductAdsList data={data} isLoading={isLoading} noFading={noFading} />;
}

export function SearchPageProductAdsList({ contextItemId, noFading }: { contextItemId: string; noFading?: boolean }) {
  const { isLoading, data } = useCountryFilteredProductAds(contextItemId);
  return <ProductAdsList data={data} isLoading={isLoading} noFading={noFading} />;
}

function ProductAdsList({ data, isLoading, noFading = false }: ProductAdsListProps) {
  const intl = useIntl();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
  });
  const [slidesInView, setSlidesInView] = useState<number[]>([]);

  const findSlidesInView = useCallback(() => {
    if (!emblaApi) return;

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView);
      }
      const inView = emblaApi.slidesInView().filter((index) => !slidesInViewElements.includes(index));
      return slidesInViewElements.concat(inView);
    });
  }, [emblaApi, setSlidesInView]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('slidesInView', findSlidesInView);
    emblaApi.on('reInit', findSlidesInView);
  }, [emblaApi, findSlidesInView]);

  const adsLoadings = [];
  for (let i = 0; i < 5; i++) {
    adsLoadings.push(
      <ProductAdWrapper>
        <ProductAdLoading />
      </ProductAdWrapper>
    );
  }

  if (isLoading) {
    const loadingContent = (
      <Slider>
        <Wrapper>{adsLoadings}</Wrapper>
      </Slider>
    );
    return noFading ? loadingContent : <FaddingWrapper>{loadingContent}</FaddingWrapper>;
  }

  if (!data) {
    return (
      <Banner $type="info">
        <Typography>{intl.formatMessage({ id: 'product_ads_no_data' })}</Typography>
      </Banner>
    );
  }

  const content = (
    <Slider ref={emblaRef}>
      <Wrapper>
        {data.map((productAd, index) => (
          <ProductAdWrapper key={productAd.adId}>
            <ProductAdComponent data={productAd} inView={slidesInView.includes(index)} />
          </ProductAdWrapper>
        ))}
      </Wrapper>
    </Slider>
  );

  return noFading ? content : <FaddingWrapper>{content}</FaddingWrapper>;
}
